import React, { useState, useEffect } from 'react';

import { Typography, Grid, Button } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import Hearts from './FindingASolutionHearts';
import pinkShirtsImg from '../assets/pinkShirts.jpg';
import playButton from '../assets/playAudio3.svg';
import pauseButton from '../assets/pauseAudio.svg';
import venusAudio1 from '../assets/audio/findingVenus.wav';
import venusAudio2 from '../assets/audio/findingVenus2.wav';
import pattiAudio from '../assets/audio/findingPatti.wav';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginTop: '6.25rem'
  },
  imgCont: {
    width: '51%',
    flexWrap: 'nowrap',
    marginBottom: '2.2rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexWrap: 'wrap'
    }
  },
  imgCap: {
    width: '100%',
    marginTop: '1rem',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      width: '75%',
      marginTop: '1rem',
      textAlign: 'center'
    }
  },
  textBlock1: {
    width: '51%',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  textWAudio: {
    width: '51%',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  textBlock2: {
    width: '81%',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  audioCaptions: {
    width: '100%',
    marginLeft: '3rem',
    marginBottom: '2.125rem'
  },
  audioCaptionCont: {
    width: '13%',
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  button: {
    width: '88%',
    padding: 0,
    marginTop: '.75rem',
    '&:hover': {
      transform: 'scale(1.1,1.1)'
    }
  },
  button1: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none'
    }
  },
  boldYellow: {
    color: theme.palette.common.yellow,
    fontFamily: 'proximaNovaB'
  },
  boldFont: {
    fontFamily: 'proximaNovaB'
  },
  //   ------------------------------------------------------------------------------------ mobile
  mobileWrapper: {
    flexWrap: 'nowrap',
    overflowX: 'scroll'
  },
  mobileAudioCapCont: {
    marginTop: '3.125rem',
    minWidth: '278px',
    textAlign: 'center',
    padding: '2rem 0',
    borderTop: '1px solid black',
    borderBottom: '1px solid black'
  },
  buttonMobile: {
    width: '40%'
  },
  mobileCaptionText: {
    height: '150px'
  },
  //   --------------------------------------
  heartGraphicsCont: {
    width: '51%',
    margin: '3.125rem 0',
    [theme.breakpoints.down('md')]: {
      width: '40%'
    },
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  lastTextBlock: {
    width: '51%',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  }
}));

const nameToAudio = {
  venus1: new Audio(venusAudio1),
  venus2: new Audio(venusAudio2),
  patti: new Audio(pattiAudio)
};

const FindingASolution = () => {
  const classes = useStyles();
  const theme = useTheme();
  const matchesSM = useMediaQuery(theme.breakpoints.down('sm'));
  const [isPlaying, setIsPlaying] = useState(false);
  const [personPlaying, setPersonPlaying] = useState('');

  const { ref, inView } = useInView({});
  const animation = useAnimation();

  useEffect(() => {
    if (inView) {
      animation.start({
        opacity: 1,

        transition: {
          type: 'spring',
          duration: 3
        }
      });
    }
    if (!inView) {
      animation.start({ opacity: 0 });
    }
  }, [inView, animation]);

  const audioClickHandler = (name) => {
    if (isPlaying) {
      nameToAudio[personPlaying].pause();
      setIsPlaying(false);
      if (personPlaying !== name) {
        nameToAudio[name].play();
        setIsPlaying(true);
      }
    } else {
      nameToAudio[name].play();
      setIsPlaying(true);
    }
  };

  const mobileAudioCaption = (
    <Grid container className={classes.mobileWrapper}>
      {/* ------------------------------------------------------------------------------ patti  */}
      <Grid container justifyContent="center" className={classes.mobileAudioCapCont}>
        <Typography variant="body2" className={classes.mobileCaptionText}>
          "When you have a building or a bridge and you start seeing structural damage ... you know
          that you need to repair it."
          <br />
          <span className={classes.boldFont}>Venus Gines</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler('venus1');
            setPersonPlaying('venus1');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'venus1' ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
      {/* ------------------------------------------------------------------------------ karen  */}
      <Grid
        container
        justifyContent="center"
        className={classes.mobileAudioCapCont}
        style={{ marginLeft: '1rem', marginRight: '1rem' }}
      >
        <Typography variant="body2" className={classes.mobileCaptionText}>
          "A promotora is a community health worker ... they understand those system barriers."
          <br />
          <span className={classes.boldFont}>Venus Gines</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler('venus2');
            setPersonPlaying('venus2');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'venus2' ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
      {/* ------------------------------------------------------------------------------ erika  */}
      <Grid container justifyContent="center" className={classes.mobileAudioCapCont}>
        <Typography variant="body2" className={classes.mobileCaptionText}>
          "At first I was really hesitant to talk to my doctor..."
          <br />
          <span className={classes.boldFont}>Patti Murillo-Casa</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler('patti');
            setPersonPlaying('patti');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'patti' ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <Grid container direction="column" alignItems="center" className={classes.wrapper}>
      <Grid
        ref={ref}
        container
        direction="column"
        alignItems={matchesSM ? 'center' : 'flex-start'}
        className={classes.imgCont}
      >
        <motion.img
          animate={animation}
          src={pinkShirtsImg}
          alt="ladies in pink shirts"
          style={{ width: '100%' }}
        />
        <Typography variant="caption" className={classes.imgCap}>
          Karen Eubanks Jackson, and other supporters, during a breast cancer awarness charity walk.
        </Typography>
      </Grid>
      <Grid container className={classes.textBlock1}>
        <Typography variant="h2" style={{ marginBottom: '3.125rem', width: '80%' }}>
          Finding a Solution Through Community Care
        </Typography>
      </Grid>
      <Grid container className={classes.textWAudio}>
        <Grid container className={classes.textBlock2}>
          <Typography variant="body1" style={{ marginBottom: '3.125rem' }}>
            What all of this data makes clear is that no single solution will{' '}
            <span className={classes.boldYellow}>create balance </span>in the disparities that Black
            and Hispanic women face when it comes to their health. Without big, systemic changes to
            the way this country’s healthcare, economic, and infrastructural policies work, these
            women will continue to see poorer cancer outcomes than women with more societal
            advantages.
          </Typography>
          <Typography variant="body1">
            In the meantime, it’s important to facilitate conversation and share information.
            Research has shown that even though Black and Hispanic women are disproportionately
            affected by cancer, they are less likely to advocate for themselves. The hope is that if
            women are better informed about their rate of risk and about their treatment options,
            they can work both within their{' '}
            <span className={classes.boldYellow}>support networks </span>and with their care
            providers to come up with better, proactive solutions. By no means should women be
            expected to shoulder the burden of their own care plans– ultimately it’s the{' '}
            <span className={classes.boldYellow}>medical industry’s responsibility </span> to offer
            more inclusive and innovative practices– but research has shown time and time again that
            informed self-advocacy has the potential to save lives.
          </Typography>
        </Grid>
        <Grid container direction="column" className={classes.audioCaptionCont}>
          {/* ---------------------------------------------------------------------------------------- venus1  */}
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            className={classes.audioCaptions}
          >
            <Typography variant="caption">
              "When you have a building or a bridge and you start seeing structural damage ... you
              know that you need to repair it."
              <br />
              <span className={classes.boldFont}>Venus Gines</span>
            </Typography>
            <Button
              disableRipple
              disableFocusRipple
              className={classes.button1}
              onClick={() => {
                audioClickHandler('venus1');
                setPersonPlaying('venus1');
              }}
            >
              <img
                src={isPlaying && personPlaying === 'venus1' ? pauseButton : playButton}
                alt="playButton"
                className={classes.button}
                style={{ width: '100%' }}
              />
            </Button>
          </Grid>
          {/* ---------------------------------------------------------------------------------------- venus2 */}
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            className={classes.audioCaptions}
          >
            <Typography variant="caption">
              "A promotora is a community health worker ... they understand those system barriers."
              Venus Gines
              <br />
              <span className={classes.boldFont}>Venus Gines</span>
            </Typography>
            <Button
              disableRipple
              disableFocusRipple
              className={classes.button1}
              onClick={() => {
                audioClickHandler('venus2');
                setPersonPlaying('venus2');
              }}
            >
              <img
                src={isPlaying && personPlaying === 'venus2' ? pauseButton : playButton}
                alt="playButton"
                className={classes.button}
                style={{ width: '100%' }}
              />
            </Button>
          </Grid>
          {/* ---------------------------------------------------------------------------------------- Erika  */}
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            className={classes.audioCaptions}
          >
            <Typography variant="caption">
              "At first I was really hesitant to talk to my doctor..."
              <br />
              <span className={classes.boldFont}>Patti Murillo-Casa</span>
            </Typography>
            <Button
              disableRipple
              disableFocusRipple
              className={classes.button1}
              onClick={() => {
                audioClickHandler('patti');
                setPersonPlaying('patti');
              }}
            >
              <img
                src={isPlaying && personPlaying === 'patti' ? pauseButton : playButton}
                alt="playButton"
                className={classes.button}
                style={{ width: '100%' }}
              />
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {matchesSM ? mobileAudioCaption : null}
      <Hearts />
      <Grid container justifyContent="center" className={classes.lastTextBlock}>
        <Typography variant="body2" style={{ width: '71%', textAlign: 'center' }}>
          Although black women are less likely to advocate for themselves, in general, women who
          receive more health & wellness information are{' '}
          <span className={classes.boldFont}> 5x </span>more likely to speak with a doctor about
          their care options.
          <sup style={{ fontFamily: 'proximaNovaReg', fontSize: '.8rem' }}> 17</sup>
        </Typography>
      </Grid>
    </Grid>
  );
};

export default FindingASolution;
